import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngxs/store';
import { AuthState } from '../../auth/auth.state';
import { BillyticsPublicContainerComponent } from '../billytics-public-container-component/billytics-public-container-component';
import { LoadingEnd, LoadingStart, LoadingState } from '../../../core/loading.state';
import { finalize, from } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-home.component',
  imports: [
    MatExpansionModule,
    MatCardModule,
    MatButtonModule,
    BillyticsPublicContainerComponent,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent {
  #auth = inject(Auth);
  #router = inject(Router);
  #store = inject(Store);
  date = new Date();

  isLoading$ = this.#store.selectSignal(LoadingState.isLoading(this.constructor.name));

  firebaseUser$ = this.#store.selectSignal(AuthState.firebaseUser);

  faqs = [
    {
      question: 'What is Billytics?',
      answer:
        'Billytics is an AI-powered platform that helps you organize and analyze your financial documents. It centralizes bills, receipts, and subscriptions from your email and provides insightful analytics to simplify financial management.',
    },
    {
      question: 'How does Billytics fetch my bills and receipts?',
      answer:
        'Billytics securely integrates with your email account to scan for bills, receipts, and invoices. It uses advanced algorithms to extract relevant details and organizes them in one centralized dashboard.',
    },
    {
      question: 'Is my data secure with Billytics?',
      answer:
        'Absolutely! Billytics prioritizes your privacy and security. We use industry-standard encryption protocols to protect your data and never share it with third parties without your consent.',
    },
    {
      question: 'Can Billytics track subscriptions and send reminders?',
      answer:
        'Yes, Billytics tracks all your active subscriptions and sends timely reminders for upcoming renewals or payments, ensuring you never miss a due date.',
    },
    {
      question: 'Does Billytics support receipt scanning?',
      answer:
        'Yes, you can upload or scan receipts directly into Billytics from your phone. Our AI-powered system extracts and organizes the details for easy tracking and analysis.',
    },
    {
      question: 'Can I use Billytics for business expenses?',
      answer:
        'Absolutely! Billytics is designed for both personal and business use. Businesses can manage invoices, analyze spending, and simplify financial workflows with ease.',
    },
    {
      question: 'How do I get started with Billytics?',
      answer:
        'Getting started is simple! Sign up for an account, link your email, and let Billytics do the rest. Your bills, receipts, and subscriptions will be organized in minutes. For older data, you can manually fetch past records, and Billytics will then automatically fetch all future bills.',
    },
  ];

  openAuth() {
    this.#store.dispatch(new LoadingStart(this.constructor.name));
    const url = this.#auth.currentUser ? 'portal/dashboard/content' : '/auth';
    from(this.#router.navigateByUrl(url))
      .pipe(finalize(() => this.#store.dispatch(new LoadingEnd(this.constructor.name))))
      .subscribe();
  }

  async openContactUs() {
    await this.#router.navigateByUrl('contact-us');
  }
}
